/**
 * GraphExplorer — Full-page knowledge graph exploration interface
 * 
 * This page provides:
 * - Entity and document selection controls
 * - Configuration options (hops, max nodes, show chunks)
 * - Graph visualization using the shared ForceGraphCanvas component
 * 
 * The actual graph rendering is delegated to ForceGraphCanvas,
 * which is the single source of truth for all graph visualization.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Tabs,
    Tab,
    Typography,
    TextField,
    Button,
    Slider,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Paper,
    CircularProgress,
    Autocomplete,
    Switch,
} from '@mui/material';

import { ForceGraphCanvas, GraphNode, GraphLink, NODE_COLORS } from '../../components/Graph';
import { API_URL } from '../../lib/constants';
import { usePersona } from '../../lib/hooks/UsePersona';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';

interface Entity {
    id: string;
    name: string;
}

interface Document {
    id: string;
    source: string;
}

interface APIGraphNode {
    id: string;
    label: string;
    type: 'Document' | 'Chunk' | 'Entity';
    text?: string;
}

interface APIGraphEdge {
    source: string;
    target: string;
    type: string;
    predicate?: string;
}

const GraphExplorer: React.FC = () => {
    const { getPersonas } = usePersona();
    const { userInfo } = useUserInfoContext();
    const [availableTenants, setAvailableTenants] = useState<string[]>(['chip', 'ed']);
    const [selectedTenant, setSelectedTenant] = useState<string>('chip');

    // Tab and selection state
    const [activeTab, setActiveTab] = useState(0);
    const [entities, setEntities] = useState<Entity[]>([]);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
    const [selectedDocument, setSelectedDocument] = useState<string>('');

    // Configuration state
    const [hops, setHops] = useState(2);
    const [maxNodes, setMaxNodes] = useState(500);
    const [showChunks, setShowChunks] = useState(false);

    // Data state
    const [rawGraphData, setRawGraphData] = useState<{ nodes: GraphNode[]; links: GraphLink[] }>({ nodes: [], links: [] });
    const [graphData, setGraphData] = useState<{ nodes: GraphNode[]; links: GraphLink[] }>({ nodes: [], links: [] });

    // Loading state
    const [loading, setLoading] = useState(false);
    const [loadingLists, setLoadingLists] = useState(true);

    // Fetch available tenants from personas
    useEffect(() => {
        if (!userInfo) return;

        const fetchTenants = async () => {
            try {
                const personas = await getPersonas();
                const tenants = Array.from(new Set(personas.map(p => p.tenantId || 'chip'))).sort();
                if (tenants.length > 0) {
                    setAvailableTenants(tenants);
                    // Default to chip if available, otherwise first one
                    if (tenants.includes('chip')) {
                        setSelectedTenant('chip');
                    } else {
                        setSelectedTenant(tenants[0]);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch personas for tenants:', error);
            }
        };
        fetchTenants();
    }, [getPersonas, userInfo]);

    // Fetch entities and documents on mount or when tenant changes
    useEffect(() => {
        const fetchLists = async () => {
            setLoadingLists(true);
            try {
                const [entitiesRes, documentsRes] = await Promise.all([
                    fetch(`${API_URL}/graph/entities?tenantId=${selectedTenant}`),
                    fetch(`${API_URL}/graph/documents?tenantId=${selectedTenant}`),
                ]);
                const entitiesData = await entitiesRes.json();
                const documentsData = await documentsRes.json();
                setEntities(entitiesData.entities || []);
                setDocuments(documentsData.documents || []);
            } catch (error) {
                console.error('Failed to fetch lists:', error);
            } finally {
                setLoadingLists(false);
            }
        };
        fetchLists();
    }, [selectedTenant]);

    // Fetch subgraph data
    const fetchSubgraph = useCallback(async (nodeIds: string[]) => {
        if (nodeIds.length === 0) return;

        setLoading(true);
        try {
            const params = new URLSearchParams({
                tenantId: selectedTenant,
                nodeIds: nodeIds.join(','),
                hops: hops.toString(),
                limit: maxNodes.toString(),
            });

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 180000);

            const res = await fetch(`${API_URL}/graph/subgraph?${params}`, {
                signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }
            const data = await res.json();

            // Transform to our format
            const transformedNodes: GraphNode[] = (data.nodes || []).map((n: APIGraphNode) => ({
                id: n.id,
                label: n.label,
                type: n.type,
                text: n.text,
            }));

            const transformedLinks: GraphLink[] = (data.edges || []).map((e: APIGraphEdge) => ({
                source: e.source,
                target: e.target,
                type: e.type,
                label: e.predicate || e.type,
            }));

            setRawGraphData({ nodes: transformedNodes, links: transformedLinks });
        } catch (error: any) {
            if (error.name === 'AbortError') {
                console.error('Request timed out');
            } else {
                console.error('Failed to fetch subgraph:', error);
            }
        } finally {
            setLoading(false);
        }
    }, [hops, maxNodes, selectedTenant]);

    // Reset selections when tenant changes
    useEffect(() => {
        setSelectedEntities([]);
        setSelectedDocument('');
        setRawGraphData({ nodes: [], links: [] });
    }, [selectedTenant]);

    // Filter graph data based on showChunks toggle
    useEffect(() => {
        let nodes: GraphNode[];
        let links: GraphLink[];

        if (showChunks) {
            nodes = [...rawGraphData.nodes];
            links = [...rawGraphData.links];
        } else {
            // Filter out Chunk nodes and their edges
            nodes = rawGraphData.nodes.filter(n => n.type !== 'Chunk');
            const filteredNodeIds = new Set(nodes.map(n => n.id));

            const getLinkNodeId = (node: string | GraphNode): string => {
                if (typeof node === 'string') return node;
                if (typeof node === 'object' && node !== null) return node.id;
                return '';
            };

            links = rawGraphData.links.filter(l => {
                const sourceId = getLinkNodeId(l.source);
                const targetId = getLinkNodeId(l.target);
                return filteredNodeIds.has(sourceId) && filteredNodeIds.has(targetId);
            });
        }

        console.log(`[GraphExplorer] ${nodes.length} nodes, ${links.length} links`);
        setGraphData({ nodes, links });
    }, [rawGraphData, showChunks]);

    const handleExploreEntities = () => {
        const nodeIds = selectedEntities.map(e => e.id);
        fetchSubgraph(nodeIds);
    };

    const handleDocumentChange = (event: any) => {
        const docId = event.target.value;
        setSelectedDocument(docId);
        if (docId) {
            fetchSubgraph([docId]);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 1.5, md: 2 },
                height: '100%',
                minHeight: 0,
                minWidth: 0,
                width: '100%',
                overflow: 'hidden',
                p: { xs: 1, sm: 2 },
            }}
        >
            {/* Left Panel - Controls */}
            <Paper
                sx={{
                    flex: { xs: '0 0 auto', md: '0 0 350px' },
                    width: { xs: '100%', md: 350 },
                    maxWidth: '100%',
                    maxHeight: { xs: '46dvh', md: 'none' },
                    minHeight: 0,
                    minWidth: 0,
                    p: { xs: 1.5, sm: 2 },
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    Graph Explorer
                </Typography>

                <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)' }}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel shrink>Select Graph</InputLabel>
                        <Select
                            value={selectedTenant}
                            label="Select Graph"
                            notched
                            onChange={(e) => setSelectedTenant(e.target.value)}
                            sx={{ bgcolor: 'background.paper' }}
                        >
                            {availableTenants.map((t) => (
                                <MenuItem key={t} value={t}>
                                    {t.toUpperCase()}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                    <Tab label="Entities" />
                    <Tab label="Documents" />
                </Tabs>

                {loadingLists ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {activeTab === 0 && (
                            <Box>
                                <Autocomplete
                                    multiple
                                    options={entities}
                                    getOptionLabel={(option) => option.name}
                                    value={selectedEntities}
                                    onChange={(_, newValue) => setSelectedEntities(newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Search Entities" placeholder="Type to search..." />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip label={option.name} {...getTagProps({ index })} size="small" />
                                        ))
                                    }
                                    sx={{ mb: 2 }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleExploreEntities}
                                    disabled={selectedEntities.length === 0 || loading}
                                    fullWidth
                                >
                                    Explore Selected
                                </Button>
                            </Box>
                        )}

                        {activeTab === 1 && (
                            <FormControl fullWidth>
                                <InputLabel>Select Document</InputLabel>
                                <Select
                                    value={selectedDocument}
                                    label="Select Document"
                                    onChange={handleDocumentChange}
                                >
                                    {documents.map((doc) => (
                                        <MenuItem key={doc.id} value={doc.id}>
                                            {doc.source}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <Box sx={{ mt: 4 }}>
                            <Typography gutterBottom>Hops: {hops}</Typography>
                            <Slider
                                value={hops}
                                onChange={(_, v) => setHops(v as number)}
                                min={1}
                                max={3}
                                marks
                                step={1}
                            />
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <Typography gutterBottom>Max Nodes: {maxNodes}</Typography>
                            <Slider
                                value={maxNodes}
                                onChange={(_, v) => setMaxNodes(v as number)}
                                min={100}
                                max={1000}
                                step={100}
                            />
                        </Box>

                        <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showChunks}
                                        onChange={(e) => setShowChunks(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Show Chunks"
                            />
                            <Typography variant="caption" display="block" color="text.secondary">
                                {showChunks ? 'Showing all nodes' : 'Entity ontology view'}
                            </Typography>
                        </Box>

                        <Box sx={{ mt: 4 }}>
                            <Typography variant="caption" color="textSecondary">
                                Legend:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                <Chip size="small" label="Document" sx={{ bgcolor: NODE_COLORS.Document, color: 'white' }} />
                                {showChunks && (
                                    <Chip size="small" label="Chunk" sx={{ bgcolor: NODE_COLORS.Chunk, color: 'white' }} />
                                )}
                                <Chip size="small" label="Entity" sx={{ bgcolor: NODE_COLORS.Entity, color: 'white' }} />
                            </Box>
                        </Box>

                        {graphData.nodes.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" fontWeight="medium">
                                    {graphData.nodes.length} nodes, {graphData.links.length} edges
                                </Typography>
                                <Typography variant="caption" color="text.secondary" component="div">
                                    {graphData.nodes.filter(n => n.type === 'Entity').length} entities,
                                    {' '}{graphData.nodes.filter(n => n.type === 'Document').length} docs
                                    {showChunks && `, ${graphData.nodes.filter(n => n.type === 'Chunk').length} chunks`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" component="div">
                                    {graphData.links.filter(l => l.type === 'FACT').length} FACT edges
                                    {showChunks && `, ${graphData.links.filter(l => l.type === 'MENTIONS').length} MENTIONS`}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Paper>

            {/* Right Panel - Graph Visualization */}
            <Paper
                sx={{
                    flex: '1 1 auto',
                    width: '100%',
                    minHeight: { xs: 280, sm: 360, md: 0 },
                    minWidth: 0,
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 2,
                }}
            >
                {loading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10,
                        }}
                    >
                        <CircularProgress size={60} />
                    </Box>
                )}

                {graphData.nodes.length === 0 && !loading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: '#666',
                        }}
                    >
                        <Typography>Select entities or a document to explore the graph</Typography>
                    </Box>
                ) : (
                    <ForceGraphCanvas
                        graphData={graphData}
                        width="100%"
                        height="100%"
                        showControls
                    />
                )}
            </Paper>
        </Box>
    );
};

export default GraphExplorer;
